import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { combineLatest, from, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ExecutorEntity, StepAction, StepTask } from '@telespot/domain';
import {
  CategoryUpsertConflict,
  FormSignatureService,
  GenerateFindingValue,
  LocalizedName,
  PipelineStepCategory,
  PipelineStepDraftProjector,
  PipelineStepParamsBuilder,
  PipelineStepStructuredView,
  PipelineIntegrityService,
} from '@protocols/domain';
import type {
  IResolveLabelNamesQuery,
  IRetrievePipelineStepDetailQuery,
  PipelineStepDraftDto,
  PipelineStepManualDraftDto,
  PipelineStepStructuredDraftDto,
} from '@protocols/use-cases';

import {
  ManualModeSeed,
  PipelineStepEditorContext,
  PipelineStepEditorInput,
  PipelineStepEditorState,
  PipelineStepSaveSnapshot,
} from '../state/pipeline-step-editor.state';
import { AlgorithmSwitcherState } from '../state/algorithm-switcher.state';
import { PipelineStepDetailState } from '../state/pipeline-step-detail.state';

export const RETRIEVE_PIPELINE_STEP_DETAIL_QUERY = new InjectionToken<IRetrievePipelineStepDetailQuery>(
  'RETRIEVE_PIPELINE_STEP_DETAIL_QUERY'
);

export const RESOLVE_LABEL_NAMES_QUERY = new InjectionToken<IResolveLabelNamesQuery>('RESOLVE_LABEL_NAMES_QUERY');

export interface PipelineStepEditorLoadInput {
  stepId: string;
  context: PipelineStepEditorContext;
}

/** One-shot result of an editing session, emitted on `confirmed$` when the user saves or cancels. */
export type PipelineStepEditorOutcome = { kind: 'saved'; step: PipelineStepDraftDto } | { kind: 'cancelled' };

const PipelineStepEditorErrorKeys = {
  duplicateCategory: 'pipeline_step_editor.errors.duplicate_category',
  singleCategoryOnly: 'pipeline_step_editor.errors.single_category_only',
  invalidState: 'pipeline_step_editor.errors.invalid_state',
  loadFailed: 'pipeline_step_editor.errors.load_failed',
  invalidManualParams: 'pipeline_step_editor.errors.invalid_manual_params',
  labelNamesLoadFailed: 'pipeline_step_editor.errors.label_names_load_failed',
} as const;

/**
 * The hint the embedded algorithm switcher shows inside this editor: here the
 * selector picks the step's executor rather than switching a live algorithm.
 */
const EXECUTOR_HINT_KEY = 'pipeline_step_editor.executor.hint';

const CONFLICT_TO_ERROR_KEY: Record<CategoryUpsertConflict, string> = {
  duplicate: PipelineStepEditorErrorKeys.duplicateCategory,
  single_category_only: PipelineStepEditorErrorKeys.singleCategoryOnly,
};

/**
 * Display names for a set of uuids, and the message to report alongside them.
 * The names are usable either way — a failed read simply leaves the uuids that
 * were only known by a placeholder still showing it — but the caller has to be
 * able to tell "these uuids have no label" from "we could not ask".
 */
interface ResolvedDisplayNames {
  names: Record<string, LocalizedName>;
  labelNamesError: string | null;
}

/** A manual draft ready to be written into the structured form. */
interface ManualDraftApplication extends ResolvedDisplayNames {
  projected: PipelineStepStructuredView;
}

interface PipelineStepDraftMeta {
  id?: string;
  pipelineId: string;
  order: number;
  task: StepTask;
  executorEntity: ExecutorEntity;
  executorId?: string;
  trigger: StepAction;
  generateFinding: GenerateFindingValue;
}

@Injectable()
export class PipelineStepEditorEffects implements OnDestroy {
  /**
   * One-shot save/cancel outcome. Event, not state — never replays to late
   * subscribers, so a reopened dialog can't consume a prior session's result.
   */
  private readonly _confirmed$ = new Subject<PipelineStepEditorOutcome>();
  public readonly confirmed$: Observable<PipelineStepEditorOutcome> = this._confirmed$.asObservable();

  constructor(
    private readonly editorState: PipelineStepEditorState,
    private readonly algorithmSwitcherState: AlgorithmSwitcherState,
    private readonly pipelineStepDetailState: PipelineStepDetailState,
    @Inject(RETRIEVE_PIPELINE_STEP_DETAIL_QUERY)
    private readonly retrievePipelineStepDetailQuery: IRetrievePipelineStepDetailQuery,
    @Inject(RESOLVE_LABEL_NAMES_QUERY)
    private readonly resolveLabelNamesQuery: IResolveLabelNamesQuery
  ) {}

  /**
   * Live canonical signature of the editing session. Single source for both
   * the facade's `hasChanges$` compare and `captureBaseline`'s snapshot.
   *
   * NOTE: declared above `captureBaseline` on purpose — its initializer reads
   * this property at construction time.
   */
  public readonly currentSignature$: Observable<string> = this.editorState.select(
    this.editorState.saveSnapshot$,
    this.algorithmSwitcherState.algorithmToSwitchToId$,
    (snapshot: PipelineStepSaveSnapshot, algorithmId: string | undefined) =>
      FormSignatureService.compute(this.projectSignature(snapshot, algorithmId))
  );

  /** Emit the dialog outcome as a one-shot event (see `confirmed$`). */
  public readonly confirm = this.editorState.effect<PipelineStepEditorOutcome>((outcome$: Observable<PipelineStepEditorOutcome>) =>
    outcome$.pipe(tap((outcome: PipelineStepEditorOutcome) => this._confirmed$.next(outcome)))
  );

  public readonly initialize = this.editorState.effect((input$: Observable<PipelineStepEditorInput>) =>
    input$.pipe(
      tap((input) => {
        this.editorState.initialize(input);
        this.algorithmSwitcherState.reset();
        // Seeded after the reset, which restores the standalone dialog's hint.
        this.algorithmSwitcherState.setHintKey(EXECUTOR_HINT_KEY);
        this.algorithmSwitcherState.setData({
          currentAlgorithmId: input.step?.executorId ?? '',
          algorithmExecutionEnvironment: PipelineIntegrityService.toExecutionEnvironment(input.context.environment),
          pipelineId: input.context.pipelineId,
          pipelineName: '',
          pipelineStepId: '',
          pipelineUsage: [],
        });
        if (input.step?.executorEntity === ExecutorEntity.ALGORITHM && input.step.executorId) {
          this.algorithmSwitcherState.setAlgorithmToSwitchToId(input.step.executorId);
        }
        this.captureBaseline();
      }),
      switchMap(() => combineLatest([this.editorState.task$, this.editorState.categories$])),
      tap(([task, categories]: [StepTask | null, PipelineStepCategory[]]) => {
        if (!task) return;
        const options = categories.flatMap((c) => c.options);
        const outputLabels = new Set<string>(options.map((o) => o.uuid));
        const outputLabelNames = Object.fromEntries(options.map((o) => [o.uuid, o.name]));
        this.pipelineStepDetailState.setStep({ task, outputLabels, outputLabelNames });
      })
    )
  );

  public readonly loadStep = this.editorState.effect((input$: Observable<PipelineStepEditorLoadInput>) =>
    input$.pipe(
      tap(() => {
        this.editorState.setLoading(true);
        this.editorState.setError(null);
      }),
      switchMap((input) =>
        from(this.retrievePipelineStepDetailQuery.execute(input.stepId)).pipe(
          tapResponse(
            (result) => {
              this.initialize({
                context: input.context,
                step: result.structured,
              });

              // If the persisted params carry data the structured form can't
              // represent (custom keys added via Manual Mode), open in Manual
              // Mode so the original JSON is visible and editable
              if (!PipelineStepParamsBuilder.isStructuredCompatible(result.rawParams)) {
                this.editorState.enterManualMode({ value: result.rawParams });
              }

              this.captureBaseline();
              this.editorState.setLoading(false);
            },
            () => {
              this.editorState.setLoading(false);
              this.editorState.setError(PipelineStepEditorErrorKeys.loadFailed);
            }
          )
        )
      )
    )
  );

  /**
   * Seeds the editor from an in-memory draft (one the parent pipeline editor
   * is holding before persisting). Mirrors `loadStep` minus the DB round-trip:
   * structured drafts hydrate the form; manual drafts hydrate the meta fields
   * and enter Manual Mode immediately so the user's hand-edited params survive.
   *
   * Any uuid the draft cannot name is resolved first, so the editor displays
   * real names by construction rather than because its supplier happened to be
   * correct — a future producer of placeholders cannot silently reintroduce a
   * category list showing uuids. Names the draft already carries are reused, so
   * the ordinary seed stays synchronous and never leaves the browser. Resolved
   * before `initialize` rather than patched in after it: one hydration, one
   * baseline, and no render of a half-named form.
   *
   * A read that fails still seeds the draft — the names were never worth the
   * step not opening — but says so, so the uuids left on screen are not taken
   * for the labels' real names.
   *
   * Gated on `loading` exactly as `loadStep` is, and for the same reason: the
   * state instance is shared across dialog opens and `initialize` is its only
   * reset, so until it runs the reopened dialog is still showing the previous
   * session — its categories, its errors and its baseline. The gate keeps that
   * stale form out of the submit gate and puts the editor's spinner up; it does
   * not hide the form, which no async path in this editor does.
   */
  public readonly seedFromDraft = this.editorState.effect(
    (input$: Observable<{ context: PipelineStepEditorContext; step: PipelineStepDraftDto }>) =>
      input$.pipe(
        tap(() => {
          this.editorState.setLoading(true);
          this.editorState.setError(null);
        }),
        switchMap(({ context, step }) =>
          this.withResolvedDraftNames(step).pipe(map((resolved) => ({ context, ...resolved })))
        ),
        tap(({ context, step, labelNamesError }) => {
          // Carried by the hydration itself, so the seeded form and the reason
          // its names are uuids land in one transition.
          this.initialize({ context, step, labelNamesError });
          if (step.mode === 'manual') {
            this.editorState.enterManualMode({ value: step.params });
          }

          this.captureBaseline();
          this.editorState.setLoading(false);
        })
      )
  );

  /**
   * The draft with real names in place of the uuids it could not name, and the
   * outcome of the read that fetched them. A manual draft carries no categories
   * — its uuids are named when it leaves Manual Mode — so it passes straight
   * through with nothing to report.
   */
  private withResolvedDraftNames(
    step: PipelineStepDraftDto
  ): Observable<{ step: PipelineStepDraftDto; labelNamesError: string | null }> {
    if (step.mode !== 'structured') return of({ step, labelNamesError: null });

    return this.resolveDisplayNames(step.categories).pipe(
      map(({ names, labelNamesError }: ResolvedDisplayNames) => ({
        step: {
          ...step,
          categories: step.categories.map((category) => category.withResolvedNames(names)),
        },
        labelNamesError,
      }))
    );
  }

  /*
   * Snapshots the editor signature into `baselineSignature` so the facade's
   * `hasChanges$` can detect divergence afterwards. Snapshots the shared
   * `currentSignature$` so baseline and live signature stay in lockstep.
   *
   * Captured on every `initialize` — the one seed point all entry paths share,
   * which is what gives a create-mode session (no step) its baseline —
   * and re-captured by `loadStep`/`seedFromDraft` AFTER a possible
   * `enterManualMode`, whose `manualDraft` mutation is part of the signature;
   * the later capture must win or a manual step would open dirty.
   */
  private readonly captureBaseline = this.editorState.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.currentSignature$),
      tap(([, signature]) => this.editorState.setBaselineSignature(signature))
    )
  );

  public readonly upsertCategory = this.editorState.effect((category$: Observable<PipelineStepCategory>) =>
    category$.pipe(
      withLatestFrom(this.editorState.editingCategoryUuid$, this.editorState.categories$, this.editorState.task$),
      tap(
        ([category, editingUuid, categories, task]: [
          PipelineStepCategory,
          string | null,
          PipelineStepCategory[],
          StepTask | null
        ]) => {
          const conflict = PipelineIntegrityService.findCategoryUpsertConflict(categories, category, {
            editingUuid,
            task,
          });
          if (conflict) {
            this.editorState.setError(CONFLICT_TO_ERROR_KEY[conflict]);
            return;
          }

          if (editingUuid !== null) {
            const editingIndex = categories.findIndex((c) => c.uuid === editingUuid);
            if (editingIndex === -1) {
              this.editorState.appendCategory(category);
            } else {
              this.editorState.replaceCategoryAt({ index: editingIndex, category });
            }
            return;
          }

          this.editorState.appendCategory(category);
        }
      )
    )
  );

  public readonly save = this.editorState.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.editorState.saveSnapshot$, this.algorithmSwitcherState.algorithmToSwitchToId$),
      tap(([, snapshot, algorithmId]: [void, PipelineStepSaveSnapshot, string | undefined]) => {
        // Save manual draft
        if (snapshot.manualDraft !== null) {
          this.saveManual(snapshot, algorithmId);
          return;
        }

        // Save structured draft
        const draft = this.buildStructuredDraft(snapshot, algorithmId);
        if (!draft) {
          this.editorState.setError(PipelineStepEditorErrorKeys.invalidState);
          return;
        }

        // Set confirmed
        this.editorState.setError(null);
        this.confirm({ kind: 'saved', step: draft });
      })
    )
  );

  public readonly cancel = this.editorState.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this.confirm({ kind: 'cancelled' })))
  );

  /**
   * Freshly enter Manual Mode. This operation seeds the JSON
   * editor with a fresh build from the current form state, so any edits
   * the user made before clicking Manual Mode are reflected in the JSON.
   */
  public readonly enterManualMode = this.editorState.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.editorState.manualModeSeed$),
      tap(([, seed]: [void, ManualModeSeed]) => {
        if (!seed.context || !seed.task) {
          this.editorState.setError(PipelineStepEditorErrorKeys.invalidState);
          return;
        }

        const params = PipelineStepParamsBuilder.build({
          env: seed.context.environment,
          task: seed.task,
          categories: seed.categories,
          multiple: seed.multiple,
          allowNewOptions: seed.allowNewOptions,
          enableMosaicView: seed.enableMosaicView,
          counter: seed.counter,
        }) as unknown as Record<string, unknown>;
        this.editorState.enterManualMode({ value: params });
      })
    )
  );

  /**
   * Leave Manual Mode by APPLYING the JSON draft to the structured form. The
   * exit button is gated on `manualParamsStructuredCompatible$`, so a draft
   * that reaches here projects cleanly; when it does not (the effect is public,
   * the gate is not) nothing is applied and Manual Mode stays on, because
   * dropping the draft is exactly the data loss this replaces.
   *
   * Names the form already holds are reused synchronously — the common
   * enter/edit/exit round-trip therefore never leaves the browser. Only uuids
   * the form cannot name cost a single bulk label lookup: hand-added ones, every
   * one of them when the editor auto-entered Manual Mode and the structured form
   * was never hydrated, and ones the form knows only by a placeholder, which are
   * retried rather than left degraded.
   *
   * A lookup that fails does not cost the user their JSON: the draft is applied
   * with the uuids it carries, and the failure is reported alongside it so those
   * uuids are not mistaken for badly-defined labels.
   */
  public readonly exitManualMode = this.editorState.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.editorState.manualParams$, this.editorState.categories$),
      switchMap(([, params, currentCategories]: [void, Record<string, unknown> | null, PipelineStepCategory[]]) =>
        this.resolveManualDraftApplication(params, currentCategories)
      ),
      tap((application: ManualDraftApplication | null) => {
        if (!application) {
          this.editorState.setError(PipelineStepEditorErrorKeys.invalidManualParams);
          return;
        }

        this.editorState.exitManualMode({
          ...application.projected,
          categories: application.projected.categories.map((category) => category.withResolvedNames(application.names)),
          labelNamesError: application.labelNamesError,
        });
      })
    )
  );

  /**
   * Projects the manual draft and gathers the display names its uuids should
   * carry. `null` means the draft cannot be represented by the structured form.
   */
  private resolveManualDraftApplication(
    params: Record<string, unknown> | null,
    currentCategories: PipelineStepCategory[]
  ): Observable<ManualDraftApplication | null> {
    const projected = params ? PipelineStepDraftProjector.toStructuredView(params) : null;
    if (!projected) return of(null);

    return this.resolveDisplayNames(projected.categories, currentCategories).pipe(
      map((resolved: ResolvedDisplayNames) => ({ projected, ...resolved }))
    );
  }

  /**
   * The display names `categories` should carry: every name `namedBy` can
   * already supply, plus one bulk lookup for the uuids it cannot. Shared by the
   * two paths that hydrate the structured form from something that may only
   * know its uuids — exiting Manual Mode, where the live form is what already
   * knows the names, and seeding from a draft, which names itself and so needs
   * no second list.
   *
   * Emits synchronously when nothing is left to look up, which is the common
   * case for both.
   */
  private resolveDisplayNames(
    categories: PipelineStepCategory[],
    namedBy: PipelineStepCategory[] = categories
  ): Observable<ResolvedDisplayNames> {
    const knownNames = this.properlyDefinedNames(namedBy);
    const uuids = new Set<string>(categories.flatMap((category) => category.labelUuids()));
    // A blank uuid is reachable through hand-edited params, and is a reference
    // to nothing rather than something the label table could answer for.
    const unnamed = [...uuids].filter((uuid) => uuid && !knownNames[uuid]);
    if (unnamed.length === 0) return of({ names: knownNames, labelNamesError: null });

    return from(this.resolveLabelNamesQuery.execute(unnamed)).pipe(
      // The names already held win: they are what the user is looking at, and a
      // rename made in the form has not reached the label repository yet.
      map((resolved: Record<string, LocalizedName>) => ({
        names: { ...resolved, ...knownNames },
        labelNamesError: null,
      })),
      // A name we cannot fetch is never worth the user's draft — hydrate with
      // the uuid placeholders it already carries instead, and report the read
      // so the uuids on screen are not passed off as the labels' names. Caught
      // inside the inner stream so a failure cannot tear the effect down for
      // later attempts.
      catchError(() => of({ names: knownNames, labelNamesError: PipelineStepEditorErrorKeys.labelNamesLoadFailed }))
    );
  }

  /**
   * The names `categories` can genuinely supply for their own uuids — the live
   * form's on the Manual Mode exit, the draft's own on the seed. A uuid known
   * only by a placeholder — its own uuid, or a blank name — is left out,
   * so it is looked up again rather than staying degraded for the session: one
   * failed or empty read no longer pins uuids as display names, and a label
   * created in another tab is picked up on the next attempt. The cost is that a
   * uuid genuinely lacking a label is re-queried each time, which is one entry
   * in a bulk read.
   */
  private properlyDefinedNames(categories: PipelineStepCategory[]): Record<string, LocalizedName> {
    const names: Record<string, LocalizedName> = {};
    for (const category of categories) {
      // Filtered per category before merging, so a placeholder in one category
      // cannot delete a real name the same uuid carries in another.
      const categoryNames = category.namesByUuid();
      for (const uuid of category.undefinedLabelUuids()) delete categoryNames[uuid];
      Object.assign(names, categoryNames);
    }
    return names;
  }

  private saveManual(snapshot: PipelineStepSaveSnapshot, algorithmId: string | undefined): void {
    if (!snapshot.manualDraft) return;
    if (!snapshot.manualDraft.valid || snapshot.manualDraft.value === null) {
      this.editorState.setError(PipelineStepEditorErrorKeys.invalidManualParams);
      return;
    }

    const meta = this.resolveStepMeta(snapshot, algorithmId);
    if (!meta) {
      this.editorState.setError(PipelineStepEditorErrorKeys.invalidState);
      return;
    }

    // If the hand-edited params can be losslessly represented by the structured
    // form — only known keys AND values that match what the form would have
    // produced from current state — emit a structured draft instead. This keeps
    // `step.mode` honest: on re-open the editor opens in structured mode
    const structuredEquivalent = this.tryBuildStructuredEquivalent(snapshot, algorithmId);
    if (structuredEquivalent) {
      this.editorState.setError(null);
      this.confirm({ kind: 'saved', step: structuredEquivalent });
      return;
    }

    // Save an actually manual draft (not a structured equivalent)
    const draft: PipelineStepManualDraftDto = {
      ...meta,
      mode: 'manual',
      params: snapshot.manualDraft.value,
    };
    this.editorState.setError(null);
    this.confirm({ kind: 'saved', step: draft });
  }

  /**
   * Try to build a structured draft that is equivalent to the current manual draft.
   * This is used to determine if the manual draft should be saved as a structured draft.
   */
  private tryBuildStructuredEquivalent(
    snapshot: PipelineStepSaveSnapshot,
    algorithmId: string | undefined
  ): PipelineStepStructuredDraftDto | null {
    if (
      !snapshot.manualDraft?.value ||
      !snapshot.task ||
      !snapshot.context ||
      !PipelineStepParamsBuilder.isStructuredCompatible(snapshot.manualDraft.value)
    ) {
      return null;
    }

    const structured = this.buildStructuredDraft(snapshot, algorithmId);
    if (!structured) return null;

    const equivalentParams = PipelineStepParamsBuilder.build({
      env: snapshot.context.environment,
      task: snapshot.task,
      categories: structured.categories,
      multiple: structured.multiple,
      allowNewOptions: structured.allowNewOptions,
      enableMosaicView: structured.enableMosaicView,
      counter: structured.counter,
    }) as unknown as Record<string, unknown>;
    if (!this.canonicalEqual(snapshot.manualDraft.value, equivalentParams)) return null;
    return structured;
  }

  private buildStructuredDraft(
    snapshot: PipelineStepSaveSnapshot,
    algorithmId: string | undefined
  ): PipelineStepStructuredDraftDto | null {
    const meta = this.resolveStepMeta(snapshot, algorithmId);
    if (!meta || !snapshot.task || !snapshot.context) return null;

    const env = snapshot.context.environment;
    const flags = PipelineIntegrityService.flagsForTask(env, snapshot.task, snapshot);

    return {
      ...meta,
      mode: 'structured',
      categories: snapshot.categories.map((category) => category.clone()),
      multiple: flags.multiple,
      allowNewOptions: flags.allowNewOptions,
      enableMosaicView: flags.enableMosaicView,
      counter: flags.counter,
    };
  }

  private resolveStepMeta(
    snapshot: PipelineStepSaveSnapshot,
    algorithmId: string | undefined
  ): PipelineStepDraftMeta | null {
    if (!snapshot.context || !snapshot.task) return null;
    const env = snapshot.context.environment;
    const trigger = PipelineIntegrityService.resolveTrigger(env, snapshot.cloudSubject);
    const generateFinding = PipelineIntegrityService.resolveGenerateFinding(
      env,
      snapshot.cloudSubject,
      snapshot.generateFinding
    );
    if (!trigger || !generateFinding || (PipelineIntegrityService.algorithmRequired(snapshot.task) && !algorithmId)) {
      return null;
    }

    const useAlgorithm = !!algorithmId && PipelineIntegrityService.algorithmCompatible(env, snapshot.task);

    return {
      id: snapshot.editingStepId,
      pipelineId: snapshot.context.pipelineId,
      order: snapshot.context.order,
      task: snapshot.task,
      executorEntity: useAlgorithm ? ExecutorEntity.ALGORITHM : ExecutorEntity.USER,
      executorId: useAlgorithm ? algorithmId : undefined,
      trigger,
      generateFinding,
    };
  }

  // ============================================================================
  // Signature projection
  // ============================================================================

  /*
   * Canonical fingerprint of everything a cancel would discard. The baseline is
   * captured at seed-completion; the facade recompares on every emission and
   * any divergence marks the session dirty. Serialized by
   * `FormSignatureService.compute` (recursive key sort), so key-insertion order
   * never flips the signature.
   *
   * Deliberate scope choices:
   *  - Categories are fingerprinted in order — reordering is real work.
   *  - `manualDraft` is included whole (value + valid): entering/editing/exiting
   *    Manual Mode is user work.
   *  - `algorithmId ?? null`: the service drops `undefined` object props, so
   *    without the coalesce an unset switcher (undefined) and an explicit null
   *    would produce diverging baselines — this preserves the old
   *    undefined ≡ null semantics.
   */
  private projectSignature(snapshot: PipelineStepSaveSnapshot, algorithmId: string | undefined): unknown {
    return {
      task: snapshot.task,
      cloudSubject: snapshot.cloudSubject,
      generateFinding: snapshot.generateFinding,
      categories: snapshot.categories.map((category) => this.toPlainCategory(category)),
      multiple: snapshot.multiple,
      allowNewOptions: snapshot.allowNewOptions,
      enableMosaicView: snapshot.enableMosaicView,
      counter: snapshot.counter,
      manualDraft: snapshot.manualDraft,
      algorithmId: algorithmId ?? null,
    };
  }

  /**
   * `PipelineStepCategory` is a class instance, which the strict signature
   * service rejects — remap to a plain object.
   */
  private toPlainCategory(category: PipelineStepCategory): unknown {
    return {
      uuid: category.uuid,
      name: { ...category.name },
      options: category.options.map((option) => ({
        uuid: option.uuid,
        name: { ...option.name },
        thrdis: option.thrdis,
      })),
    };
  }

  /**
   * Structural equality with sorted object keys, so JSON authored in any key
   * order matches the builder output. Arrays remain order-sensitive (categorization
   * order is meaningful).
   */
  private canonicalEqual(a: unknown, b: unknown): boolean {
    return this.canonicalize(a) === this.canonicalize(b);
  }

  private canonicalize(value: unknown): string {
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) return '[' + value.map((v) => this.canonicalize(v)).join(',') + ']';
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return '{' + keys.map((k) => JSON.stringify(k) + ':' + this.canonicalize(obj[k])).join(',') + '}';
  }

  ngOnDestroy(): void {
    this._confirmed$.complete();
  }
}
